import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { vpAxios, money } from "./api";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Trash2, MapPin, ArrowLeft, Search, X, Check, Printer, CreditCard, History, Clock, User, Edit3, Home, Briefcase } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";

export default function VaiPedeCardapioPublicoPage() {
  const { slug } = useParams();
  const [loja, setLoja] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [busca, setBusca] = useState("");
  const [carrinho, setCarrinho] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showCarrinho, setShowCarrinho] = useState(false);
  const [pedidoEmAndamento, setPedidoEmAndamento] = useState(null);
  const [cuponsDoDia, setCuponsDoDia] = useState([]);
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  const [showPerfil, setShowPerfil] = useState(false);

  // SEO dinâmico — título e descrição com nome real da loja
  usePageMeta({
    title: loja?.nome ? `${loja.nome} — Cardápio Digital · Peça pelo WhatsApp` : "Cardápio Digital — VAI PEDE",
    description: loja?.nome
      ? `Peça já no cardápio digital de ${loja.nome}. Entrega, retirada e pagamento pelo WhatsApp ou Pix. Rápido, fácil e sem sair de casa.`
      : "Cardápio digital com pedido em 1 toque pelo VAI PEDE. Faça seu pedido agora.",
    image: loja?.logo_url || loja?.imagem_capa || undefined,
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  useEffect(() => {
    vpAxios.get(`/vaipede/publico/${slug}`)
      .then(r => { setLoja(r.data.loja); setProdutos(r.data.produtos); setCategorias(r.data.categorias); })
      .catch(() => setLoja({ nome: "Loja não encontrada", erro: true }));
    vpAxios.get(`/vaipede/publico/${slug}/cupons`)
      .then(r => setCuponsDoDia(r.data || []))
      .catch(() => setCuponsDoDia([]));
    try {
      const stored = JSON.parse(localStorage.getItem(`vp_cli_${slug}`) || "null");
      if (stored) setCliente(stored);
      const car = JSON.parse(localStorage.getItem(`vp_cart_${slug}`) || "[]");
      if (Array.isArray(car)) setCarrinho(car);
      const emand = JSON.parse(localStorage.getItem(`vp_ped_${slug}`) || "null");
      if (emand) setPedidoEmAndamento(emand);
    } catch {}
    // Injeta manifest PWA dinâmico (substitui o padrão do React)
    try {
      const existing = document.querySelector('link[rel="manifest"]');
      const prevHref = existing?.getAttribute("href");
      if (existing) existing.setAttribute("href", `/api/vaipede/cardapio/${slug}/manifest`);
      return () => { if (existing && prevHref) existing.setAttribute("href", prevHref); };
    } catch {}
  }, [slug]);

  useEffect(() => { localStorage.setItem(`vp_cart_${slug}`, JSON.stringify(carrinho)); }, [carrinho, slug]);

  // Polling do status do pedido em andamento para notificar cliente
  const [statusPedido, setStatusPedido] = useState(null);
  useEffect(() => {
    if (!pedidoEmAndamento?.id) return;
    let anterior = null;
    const pull = async () => {
      try {
        const { data } = await vpAxios.get(`/vaipede/publico/${slug}/pedido/${pedidoEmAndamento.id}/status`);
        setStatusPedido(data);
        if (anterior && anterior !== data.status) {
          const msgs = {
            aceito: "🎉 Seu pedido foi aceito!",
            preparando: "👨‍🍳 Preparando seu pedido…",
            pronto: "✅ Pedido PRONTO!",
            saiu_entrega: `🛵 Saiu para entrega${data.eta_minutos ? ` — chega em ${data.eta_minutos} min` : ""}`,
            entregue: "🙌 Entregue! Bom apetite.",
            rejeitado: `😔 Pedido recusado${data.rejeitado_motivo ? `: ${data.rejeitado_motivo}` : ""}`,
          };
          if (msgs[data.status]) {
            toast.success(msgs[data.status], { duration: 6000 });
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              try { new Notification("VAI PEDE", { body: msgs[data.status] }); } catch {}
            }
          }
        }
        anterior = data.status;
        if (["entregue", "rejeitado", "cancelado"].includes(data.status)) {
          setTimeout(() => {
            localStorage.removeItem(`vp_ped_${slug}`);
            setPedidoEmAndamento(null);
          }, 60000);
        }
      } catch {}
    };
    pull();
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const iv = setInterval(pull, 12000);
    return () => clearInterval(iv);
  }, [pedidoEmAndamento, slug]);

  // Return handler InfinitePay: quando o cliente volta do checkout, verifica se
  // pagou. Faz polling com retry (o webhook do MP pode demorar até 30s). Só
  // cancela e reabre o pagamento se DEPOIS de várias tentativas o pedido ainda
  // estiver "pendente_pagamento".
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pagoPid = params.get("pago");
    if (!pagoPid) return;
    // limpa a query pra não repetir
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
    (async () => {
      const APROVADOS = ["aguardando", "preparando", "pronto", "saiu_entrega", "entregue", "finalizado"];
      // até 10 tentativas x 3s = 30s de tolerância pro webhook chegar
      let toastId;
      try {
        toastId = toast.loading("Confirmando pagamento…", { duration: 30000 });
        for (let i = 0; i < 10; i++) {
          await new Promise(r => setTimeout(r, i === 0 ? 3000 : 3000));
          try {
            const { data } = await vpAxios.get(`/vaipede/publico/${slug}/pedido/${pagoPid}/status`);
            if (APROVADOS.includes(data.status)) {
              // ✓ Pagamento confirmado
              toast.dismiss(toastId);
              localStorage.removeItem(`vp_carr_bkp_${slug}`);
              toast.success("✅ Pagamento aprovado! Seu pedido foi enviado.", { duration: 6000 });
              return;
            }
            if (data.status === "cancelado_pagamento" || data.status === "rejeitado" || data.status === "cancelado") {
              // já foi cancelado por algum motivo
              break;
            }
            // segue tentando enquanto for "pendente_pagamento"
          } catch (e) { /* ignora e tenta de novo */ }
        }
        // Após 30s ainda pendente → considera abandonado
        toast.dismiss(toastId);
        await vpAxios.post(`/vaipede/publico/${slug}/pedido/${pagoPid}/cancelar-pendente`).catch(() => {});
        try {
          const bkp = localStorage.getItem(`vp_carr_bkp_${slug}`);
          if (bkp) setCarrinho(JSON.parse(bkp));
        } catch {}
        localStorage.removeItem(`vp_ped_${slug}`);
        localStorage.removeItem(`vp_carr_bkp_${slug}`);
        setPedidoEmAndamento(null);
        toast.error("Pagamento não confirmado — escolha outra forma de pagamento", { duration: 6000 });
        setTimeout(() => setShowCheckout(true), 400);
      } catch (e) {
        if (toastId) toast.dismiss(toastId);
        localStorage.removeItem(`vp_carr_bkp_${slug}`);
      }
    })();
    // eslint-disable-next-line
  }, [slug]);

  const total = useMemo(() => carrinho.reduce((s, x) => s + x.preco * x.quantidade, 0), [carrinho]);
  const totalItens = carrinho.reduce((s, x) => s + x.quantidade, 0);

  const filtrados = produtos.filter(p => p.nome.toLowerCase().includes(busca.toLowerCase()));

  // Ordena categorias pela `ordem` retornada do backend (proteínas primeiro, bebidas por último)
  const categoriasOrdenadas = useMemo(() => {
    const nomes = (categorias || []).map(c => c.nome);
    // categorias que têm produto
    const comProduto = new Set(filtrados.map(p => p.categoria || "Outros"));
    const ordenadas = nomes.filter(n => comProduto.has(n));
    // extras (produtos sem categoria cadastrada) no fim
    filtrados.forEach(p => {
      const c = p.categoria || "Outros";
      if (!ordenadas.includes(c)) ordenadas.push(c);
    });
    return ordenadas;
  }, [categorias, filtrados]);

  const porCategoria = useMemo(() => {
    const acc = {};
    categoriasOrdenadas.forEach(c => { acc[c] = []; });
    filtrados.forEach(p => {
      const c = p.categoria || "Outros";
      (acc[c] = acc[c] || []).push(p);
    });
    // remove chaves vazias
    Object.keys(acc).forEach(k => { if (!acc[k].length) delete acc[k]; });
    return acc;
  }, [filtrados, categoriasOrdenadas]);

  const catRefs = useRef({});
  const [catAtiva, setCatAtiva] = useState("");

  useEffect(() => {
    // Ao carregar, define a primeira categoria como ativa (proteínas primeiro)
    const primeira = Object.keys(porCategoria)[0];
    if (primeira && !catAtiva) setCatAtiva(primeira);
  }, [porCategoria, catAtiva]);

  const scrollToCat = (cat) => {
    setCatAtiva(cat);
    const el = catRefs.current[cat];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 130;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const add = (p) => {
    setCarrinho(c => {
      const ex = c.find(x => x.produto_id === p.id);
      if (ex) return c.map(x => x.produto_id === p.id ? { ...x, quantidade: x.quantidade + 1 } : x);
      return [...c, { produto_id: p.id, nome: p.nome, categoria: p.categoria, foto_url: p.foto_url, preco: p.preco, quantidade: 1 }];
    });
    toast.success(`${p.nome} adicionado`);
  };
  const dec = (id) => setCarrinho(c => c.map(x => x.produto_id === id ? { ...x, quantidade: Math.max(0, x.quantidade - 1) } : x).filter(x => x.quantidade > 0));
  const rem = (id) => setCarrinho(c => c.filter(x => x.produto_id !== id));

  const irCheckout = () => {
    if (!carrinho.length) return toast.error("Adicione itens primeiro");
    if (!cliente) { setShowLogin(true); return; }
    setShowCheckout(true);
  };

  const onLogin = (c) => {
    setCliente(c);
    localStorage.setItem(`vp_cli_${slug}`, JSON.stringify(c));
    setShowLogin(false);
    setShowCheckout(true);
  };

  const onFinalizar = async (dadosPed) => {
    try {
      // Se o cliente escolheu salvar o novo endereço no cadastro, atualiza antes
      if (dadosPed.tipo === "delivery" && dadosPed.salvar_endereco_no_cadastro && cliente?.id) {
        try {
          const { data: atualizado } = await vpAxios.patch(
            `/vaipede/publico/${slug}/cliente/${cliente.id}`,
            {
              whatsapp_auth: cliente.whatsapp,
              endereco: dadosPed.endereco || "",
              referencia: dadosPed.referencia || "",
              cep: dadosPed.cep || "",
              rua: dadosPed.rua || "",
              numero_end: dadosPed.numero_end || "",
              complemento: dadosPed.complemento || "",
              bairro: dadosPed.bairro || "",
              cidade: dadosPed.cidade || "",
              estado: dadosPed.estado || "",
              apelido: dadosPed.apelido || "Casa",
              latitude: dadosPed.latitude ?? cliente.latitude,
              longitude: dadosPed.longitude ?? cliente.longitude,
            }
          );
          setCliente(atualizado);
          localStorage.setItem(`vp_cli_${slug}`, JSON.stringify(atualizado));
        } catch (e) {
          // Não bloqueia o pedido se falhar a atualização de perfil
          console.warn("Falha ao atualizar perfil", e);
        }
      }
      const payload = {
        cliente_id: cliente.id, cliente_nome: cliente.nome, cliente_whatsapp: cliente.whatsapp,
        endereco: dadosPed.endereco || cliente.endereco || "",
        referencia: dadosPed.referencia || cliente.referencia || "",
        cep: dadosPed.cep || "",
        rua: dadosPed.rua || "",
        numero_end: dadosPed.numero_end || "",
        complemento: dadosPed.complemento || "",
        bairro: dadosPed.bairro || "",
        cidade: dadosPed.cidade || "",
        estado: dadosPed.estado || "",
        apelido: dadosPed.apelido || "",
        latitude: dadosPed.latitude ?? cliente.latitude,
        longitude: dadosPed.longitude ?? cliente.longitude,
        tipo: dadosPed.tipo, forma_pagamento: dadosPed.forma_pagamento,
        troco_para: dadosPed.troco_para, observacao: dadosPed.observacao,
        cupom_codigo: dadosPed.cupom_codigo || "",
        regiao_id: dadosPed.regiao_id || "",
        itens: carrinho,
      };
      if (dadosPed.forma_pagamento === "infinitepay") {
        // Pagar agora via InfinitePay
        const { data } = await vpAxios.post(`/vaipede/publico/${slug}/pagamento/infinitepay`, payload);
        toast.success("Redirecionando para o pagamento seguro...");
        const ped = { id: data.pedido_id, numero: data.numero, total: data.total };
        setPedidoEmAndamento(ped);
        localStorage.setItem(`vp_ped_${slug}`, JSON.stringify(ped));
        // Salva carrinho para restaurar caso o cliente volte sem pagar
        localStorage.setItem(`vp_carr_bkp_${slug}`, JSON.stringify(carrinho));
        setCarrinho([]);
        setShowCheckout(false); setShowCarrinho(false);
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await vpAxios.post(`/vaipede/publico/${slug}/pedido`, payload);
      toast.success("Pedido enviado! Aguarde a confirmação no WhatsApp.");
      const ped = { id: data.pedido_id, numero: data.numero, total: data.total };
      setPedidoEmAndamento(ped);
      localStorage.setItem(`vp_ped_${slug}`, JSON.stringify(ped));
      setCarrinho([]);
      setShowCheckout(false); setShowCarrinho(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar pedido");
    }
  };

  const baixarRecibo = async () => {
    if (!pedidoEmAndamento?.id) return;
    try {
      const resp = await fetch(`/api/vaipede/publico/${slug}/pedido/${pedidoEmAndamento.id}/recibo-termico`);
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const safeName = `pedido-${pedidoEmAndamento.numero || pedidoEmAndamento.id}.pdf`;
      const file = new File([blob], safeName, { type: "application/pdf" });
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: safeName });
          return;
        }
      } catch (err) { if (err && err.name === "AbortError") return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = safeName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      try { window.open(url, "_blank", "noopener,noreferrer"); } catch {}
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch {
      toast.error("Erro ao baixar recibo");
    }
  };

  if (!loja) return <div className="min-h-screen grid place-items-center">Carregando cardápio...</div>;
  if (loja.erro) return <div className="min-h-screen grid place-items-center text-neutral-500">Loja não encontrada.</div>;

  const cor = loja.cor_primaria || "#EA1D2C";

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="vaipede-cardapio-publico">
      {/* Header */}
      <header className="bg-white sticky top-0 z-10 border-b border-neutral-200">
        <div className="max-w-3xl mx-auto p-4">
          <div className="flex items-center gap-3">
            {loja.logo_url ? (
              <img src={loja.logo_url} alt="Logo" className="w-14 h-14 rounded-2xl object-cover border border-neutral-200"/>
            ) : (
              <div className="w-14 h-14 rounded-2xl grid place-items-center text-white font-black text-2xl" style={{background: cor}}>
                {(loja.nome || "L")[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-black truncate">{loja.nome}</h1>
              <p className="text-xs text-neutral-500 truncate">{loja.descricao || "Peça pelo WhatsApp"}</p>
              <span className={`text-xs font-bold ${loja.aberto ? "text-emerald-600" : "text-red-600"}`}>● {loja.aberto ? "Aberto agora" : "Fechado"}</span>
            </div>
            {cliente && (
              <div className="shrink-0 flex items-center gap-1.5">
                <button
                  onClick={() => setShowPerfil(true)}
                  className="px-3 py-2 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-800 text-xs font-bold flex items-center gap-1.5 transition"
                  data-testid="vaipede-pub-btn-perfil"
                  title="Meu perfil"
                >
                  <User className="w-4 h-4"/> Meu perfil
                </button>
                <button
                  onClick={() => setShowHistorico(true)}
                  className="px-3 py-2 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-800 text-xs font-bold flex items-center gap-1.5 transition"
                  data-testid="vaipede-pub-btn-historico"
                  title="Meus pedidos"
                >
                  <History className="w-4 h-4"/> Pedidos
                </button>
              </div>
            )}
          </div>
          {pedidoEmAndamento && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-emerald-800">✅ Pedido <b>#{pedidoEmAndamento.numero}</b></span>
                <button onClick={baixarRecibo} className="text-emerald-700 font-bold text-xs underline flex items-center gap-1" data-testid="vaipede-pub-recibo">
                  <Printer className="w-3 h-3"/> Recibo
                </button>
              </div>
              <StatusTimeline status={statusPedido?.status || "aguardando"} eta={statusPedido?.eta_minutos} cor={cor}/>
            </div>
          )}
          <div className="mt-3 flex items-center gap-2 border border-neutral-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-neutral-400"/>
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar no cardápio..." className="flex-1 outline-none text-sm"/>
          </div>
        </div>
        {/* Tabs de categorias — scroll horizontal */}
        {Object.keys(porCategoria).length > 0 && (
          <div className="max-w-3xl mx-auto border-t border-neutral-100 overflow-x-auto no-scrollbar" data-testid="vaipede-pub-cat-tabs">
            <div className="flex gap-1 px-4 py-2 min-w-max">
              {Object.keys(porCategoria).map(c => {
                const active = catAtiva === c;
                const catObj = (categorias || []).find(x => x.nome === c);
                return (
                  <button
                    key={c}
                    onClick={() => scrollToCat(c)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${active ? "text-white" : "text-neutral-700 bg-neutral-100 hover:bg-neutral-200"}`}
                    style={active ? { background: cor } : {}}
                    data-testid={`vaipede-pub-cat-tab-${c}`}
                  >
                    <span className="mr-1">{catObj?.icone || "🍽️"}</span>{c}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Banner de cupons ativos hoje */}
      {cuponsDoDia.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 pt-4">
          {cuponsDoDia.map(cp => (
            <div key={cp.id} className={`rounded-2xl overflow-hidden mb-2 border-2 ${cp.esgotado ? "border-neutral-200 opacity-60" : "border-transparent"}`} style={{background: cp.banner_cor || cor}} data-testid={`vaipede-pub-cupom-${cp.id}`}>
              {cp.banner_url && <img src={cp.banner_url} alt={cp.nome} className="w-full h-32 object-cover"/>}
              <div className="p-3 text-white flex items-center justify-between">
                <div>
                  <div className="text-xs opacity-90">{cp.banner_emoji || "🎁"} Promoção do dia</div>
                  <div className="font-black">{cp.nome}</div>
                  <div className="text-xs opacity-90">
                    Código: <span className="font-mono bg-white/20 px-1 rounded">{cp.codigo}</span> ·
                    {cp.tipo_desconto === "percentual" ? ` ${cp.valor}% OFF` : ` ${money(cp.valor)} OFF`}
                  </div>
                </div>
                <div className="text-right">
                  {cp.esgotado ? (
                    <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded">ESGOTADO HOJE</span>
                  ) : cp.vagas_restantes != null ? (
                    <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded">{cp.vagas_restantes} restantes</span>
                  ) : (
                    <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded">DISPONÍVEL</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Produtos por categoria */}
      <main className="max-w-3xl mx-auto p-4 space-y-6">
        {Object.keys(porCategoria).length === 0 && <p className="text-center text-neutral-500 py-10">Nenhum produto disponível.</p>}
        {Object.entries(porCategoria).map(([cat, itens]) => {
          const catObj = (categorias || []).find(x => x.nome === cat);
          return (
          <section key={cat} ref={el => { catRefs.current[cat] = el; }} data-testid={`vaipede-pub-cat-section-${cat}`}>
            <h2 className="text-lg font-black mb-3 flex items-center gap-2">
              <span>{catObj?.icone || "🍽️"}</span>{cat}
            </h2>
            <div className="space-y-2">
              {itens.map(p => (
                <button key={p.id} onClick={() => add(p)} className="w-full text-left bg-white rounded-2xl border border-neutral-200 p-4 flex items-center gap-3 hover:border-[color:var(--pri)] transition" style={{"--pri": cor}} data-testid={`vaipede-pub-prod-${p.id}`}>
                  {p.foto_url ? (
                    <img src={p.foto_url} alt="" className="w-20 h-20 rounded-xl object-cover border border-neutral-200 shrink-0"/>
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-neutral-100 grid place-items-center text-neutral-300 shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"/></svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{p.nome}</div>
                    {p.descricao && <div className="text-xs text-neutral-500 line-clamp-1">{p.descricao}</div>}
                    <div className="font-black mt-1" style={{color: cor}}>{money(p.preco)}</div>
                  </div>
                  <div className="w-9 h-9 rounded-full grid place-items-center text-white shrink-0" style={{background: cor}}>
                    <Plus className="w-5 h-5"/>
                  </div>
                </button>
              ))}
            </div>
          </section>
          );
        })}
      </main>

      {/* Bottom cart bar */}
      {carrinho.length > 0 && (
        <div className="fixed bottom-4 inset-x-0 z-20 px-4">
          <button onClick={() => setShowCarrinho(true)} className="max-w-3xl mx-auto w-full py-4 px-5 rounded-2xl text-white font-black flex items-center justify-between shadow-xl" style={{background: cor}} data-testid="vaipede-pub-cart-open">
            <span className="flex items-center gap-2"><ShoppingBag className="w-5 h-5"/> {totalItens} itens</span>
            <span>{money(total)} · Ver pedido</span>
          </button>
        </div>
      )}

      {/* Carrinho */}
      {showCarrinho && (
        <div className="fixed inset-0 z-30 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowCarrinho(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="font-black text-lg">Seu pedido</h2>
              <button onClick={() => setShowCarrinho(false)}><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 overflow-auto flex-1 space-y-2">
              {carrinho.map(x => (
                <div key={x.produto_id} className="flex items-center gap-3 py-2 border-b border-neutral-100">
                  {x.foto_url ? (
                    <img src={x.foto_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-neutral-200 shrink-0"/>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-neutral-100 grid place-items-center text-neutral-300 shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Z"/></svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{x.nome}</div>
                    <div className="text-xs font-black" style={{color: cor}}>{money(x.preco * x.quantidade)}</div>
                  </div>
                  <button onClick={() => dec(x.produto_id)} className="p-1.5 bg-neutral-100 rounded-lg"><Minus className="w-3 h-3"/></button>
                  <span className="w-6 text-center font-bold">{x.quantidade}</span>
                  <button onClick={() => add({ id: x.produto_id, nome: x.nome, preco: x.preco, categoria: x.categoria, foto_url: x.foto_url })} className="p-1.5 rounded-lg text-white" style={{background: cor}}><Plus className="w-3 h-3"/></button>
                  <button onClick={() => rem(x.produto_id)} className="p-1.5 text-red-500"><Trash2 className="w-3 h-3"/></button>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-neutral-100">
              <div className="flex items-center justify-between mb-3 text-lg"><b>Total</b><b style={{color: cor}}>{money(total)}</b></div>
              <button onClick={irCheckout} className="w-full py-3 rounded-xl text-white font-black" style={{background: cor}} data-testid="vaipede-pub-checkout-btn">
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogin && <LoginModal slug={slug} cor={cor} onClose={() => setShowLogin(false)} onLogin={onLogin}/>}
      {showCheckout && cliente && (
        <CheckoutModal cor={cor} loja={loja} cliente={cliente} total={total} pagamentoOnline={loja.pagamento_online_disponivel} cuponsDoDia={cuponsDoDia} slug={slug} onClose={() => setShowCheckout(false)} onFinalizar={onFinalizar}/>
      )}
      {showHistorico && cliente && (
        <HistoricoModal slug={slug} cliente={cliente} cor={cor} onClose={() => setShowHistorico(false)}/>
      )}
      {showPerfil && cliente && (
        <MeuPerfilModal
          slug={slug}
          cliente={cliente}
          cor={cor}
          onClose={() => setShowPerfil(false)}
          onSaved={(c) => {
            setCliente(c);
            localStorage.setItem(`vp_cli_${slug}`, JSON.stringify(c));
            setShowPerfil(false);
          }}
          onLogout={() => {
            localStorage.removeItem(`vp_cli_${slug}`);
            setCliente(null);
            setShowPerfil(false);
            toast.success("Você saiu da conta");
          }}
        />
      )}
    </div>
  );
}

function LoginModal({ slug, cor, onClose, onLogin }) {
  const [f, setF] = useState({ nome: "", whatsapp: "", endereco: "", referencia: "", latitude: null, longitude: null });
  const [loading, setLoading] = useState(false);
  const [gps, setGps] = useState(false);
  const [modoEntrega, setModoEntrega] = useState(""); // "gps" | "manual" | ""

  const pegarLoc = () => {
    if (!navigator.geolocation) return toast.error("Sem GPS");
    setGps(true);
    setModoEntrega("gps");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // GPS ativo anula o endereço manual
        setF(v => ({ ...v, latitude: pos.coords.latitude, longitude: pos.coords.longitude, endereco: "" }));
        toast.success("📍 Localização capturada"); setGps(false);
      },
      () => { toast.error("Sem permissão de localização"); setGps(false); setModoEntrega(""); },
    );
  };

  const escolherManual = () => {
    // Endereço manual anula o GPS
    setModoEntrega("manual");
    setF(v => ({ ...v, latitude: null, longitude: null }));
  };

  const entrar = async () => {
    if (!f.nome.trim() || !f.whatsapp.trim()) return toast.error("Nome e WhatsApp são obrigatórios");
    setLoading(true);
    try {
      const { data } = await vpAxios.post(`/vaipede/publico/${slug}/cliente/login`, f);
      onLogin(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6">
        <h2 className="font-black text-xl mb-1">Quase lá!</h2>
        <p className="text-sm text-neutral-500 mb-5">Cadastre-se em 10 segundos para enviar o pedido.</p>
        <div className="space-y-2">
          <input value={f.nome} onChange={e => setF({...f, nome: e.target.value})} placeholder="Seu nome" className="w-full px-4 py-3 rounded-xl border border-neutral-200 outline-none focus:border-[color:var(--pri)]" style={{"--pri": cor}} data-testid="vaipede-pub-nome"/>
          <input type="tel" value={f.whatsapp} onChange={e => setF({...f, whatsapp: e.target.value})} placeholder="WhatsApp (com DDD)" className="w-full px-4 py-3 rounded-xl border border-neutral-200 outline-none focus:border-[color:var(--pri)]" style={{"--pri": cor}} data-testid="vaipede-pub-whatsapp"/>

          <div className="text-xs font-bold text-neutral-500 mt-2 mb-1">Como quer receber?</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={pegarLoc} disabled={gps} className={`py-3 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition ${modoEntrega === "gps" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-neutral-200 bg-white text-neutral-700 hover:border-emerald-300"}`} data-testid="vaipede-pub-btn-gps">
              <MapPin className="w-4 h-4"/>
              {f.latitude ? "✓ GPS capturado" : (gps ? "Buscando..." : "Compartilhar minha localização")}
            </button>
            <button onClick={escolherManual} type="button" className={`py-3 rounded-xl font-bold text-xs flex flex-col items-center justify-center gap-1 border-2 transition ${modoEntrega === "manual" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-neutral-200 bg-white text-neutral-700 hover:border-blue-300"}`} data-testid="vaipede-pub-btn-manual">
              <span className="text-base">✏️</span>
              Entrega em outro endereço
            </button>
          </div>
          {modoEntrega === "manual" && (
            <>
              <input value={f.endereco} onChange={e => setF({...f, endereco: e.target.value})} placeholder="Endereço completo (rua, nº, bairro, cidade)" className="w-full px-4 py-3 rounded-xl border border-neutral-200 outline-none" data-testid="vaipede-pub-endereco"/>
              <input value={f.referencia} onChange={e => setF({...f, referencia: e.target.value})} placeholder="Ponto de referência (ex: em frente à padaria)" className="w-full px-4 py-3 rounded-xl border border-neutral-200 outline-none" data-testid="vaipede-pub-referencia"/>
            </>
          )}
          {modoEntrega === "gps" && f.latitude && (
            <input value={f.referencia} onChange={e => setF({...f, referencia: e.target.value})} placeholder="Ponto de referência (opcional)" className="w-full px-4 py-3 rounded-xl border border-neutral-200 outline-none" data-testid="vaipede-pub-referencia-gps"/>
          )}
        </div>
        <button onClick={entrar} disabled={loading} className="w-full mt-5 py-3.5 rounded-xl text-white font-black disabled:opacity-60" style={{background: cor}} data-testid="vaipede-pub-entrar">
          {loading ? "Salvando..." : "Continuar"}
        </button>
      </div>
    </div>
  );
}

function CheckoutModal({ cor, loja, cliente, total, pagamentoOnline, cuponsDoDia, slug, onClose, onFinalizar }) {
  // Modo de endereço para este pedido:
  //   "cadastrado"   → usa o endereço do perfil (padrão se já houver algum)
  //   "editar"       → edita o endereço estruturado e SALVA no perfil
  //   "so_neste"     → informa um endereço diferente somente neste pedido
  const clienteTemEndereco = !!(cliente?.endereco || cliente?.rua || cliente?.latitude);
  const [modoEnd, setModoEnd] = useState(clienteTemEndereco ? "cadastrado" : "editar");
  const [addr, setAddr] = useState({
    endereco: cliente.endereco || "",
    referencia: cliente.referencia || "",
    cep: cliente.cep || "",
    rua: cliente.rua || "",
    numero_end: cliente.numero_end || "",
    complemento: cliente.complemento || "",
    bairro: cliente.bairro || "",
    cidade: cliente.cidade || "",
    estado: cliente.estado || "",
    apelido: cliente.apelido || "Casa",
    latitude: cliente.latitude ?? null,
    longitude: cliente.longitude ?? null,
  });
  const [f, setF] = useState({ tipo: "delivery", forma_pagamento: "dinheiro", troco_para: "", observacao: "", cupom_codigo: "", regiao_id: "" });
  const [enviando, setEnviando] = useState(false);
  const [descontoInfo, setDescontoInfo] = useState(null);
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [regioes, setRegioes] = useState([]);
  const [freteMin, setFreteMin] = useState(0);
  const [cepLoading, setCepLoading] = useState(false);

  // Compõe uma string "endereco" a partir dos campos estruturados quando
  // o cliente edita/informa manualmente. Backend continua recebendo o campo
  // `endereco` livre para compatibilidade com histórico existente.
  const enderecoComposto = () => {
    const partes = [];
    if (addr.rua) partes.push(addr.rua + (addr.numero_end ? `, ${addr.numero_end}` : ""));
    if (addr.complemento) partes.push(addr.complemento);
    if (addr.bairro) partes.push(addr.bairro);
    if (addr.cidade) partes.push(addr.cidade + (addr.estado ? `/${addr.estado}` : ""));
    return partes.join(" — ");
  };

  const buscarCep = async (cep) => {
    const clean = String(cep || "").replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const d = await r.json();
      if (!d.erro) {
        setAddr(a => ({
          ...a,
          rua: a.rua || d.logradouro || "",
          bairro: a.bairro || d.bairro || "",
          cidade: a.cidade || d.localidade || "",
          estado: a.estado || d.uf || "",
        }));
      }
    } catch {} finally { setCepLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await vpAxios.get(`/vaipede/publico/${slug}/entrega/regioes`);
        setRegioes(data.regioes || []);
        setFreteMin(data.frete_gratis_min || 0);
      } catch {}
    })();
  }, [slug]);

  const disponiveis = (cuponsDoDia || []).filter(c => !c.esgotado);
  const esgotados = (cuponsDoDia || []).filter(c => c.esgotado);

  const regiaoSelecionada = regioes.find(r => r.id === f.regiao_id);
  const taxaEntrega = f.tipo === "delivery" && regiaoSelecionada && !regiaoSelecionada.bloqueado
    ? (regiaoSelecionada.frete_gratis || (freteMin > 0 && (descontoInfo ? descontoInfo.total_com_desconto : total) >= freteMin)
      ? 0 : Number(regiaoSelecionada.taxa || 0))
    : 0;
  const faltaFreteGratis = f.tipo === "delivery" && freteMin > 0 && !regiaoSelecionada?.frete_gratis
    ? Math.max(0, freteMin - (descontoInfo ? descontoInfo.total_com_desconto : total))
    : 0;

  const aplicarCupom = async (codigo) => {
    setValidandoCupom(true);
    setF(x => ({...x, cupom_codigo: codigo}));
    try {
      const { data } = await vpAxios.post(`/vaipede/publico/${slug}/cupons/validar`, { codigo, subtotal: total });
      setDescontoInfo(data);
      toast.success(`Cupom aplicado! -${money(data.desconto)}`);
    } catch (e) {
      setDescontoInfo(null);
      toast.error(e?.response?.data?.detail || "Cupom inválido");
    } finally { setValidandoCupom(false); }
  };

  const removerCupom = () => {
    setF(x => ({...x, cupom_codigo: ""}));
    setDescontoInfo(null);
  };

  const totalFinal = descontoInfo ? descontoInfo.total_com_desconto : total;

  const enviar = async (metodoOverride) => {
    if (f.tipo === "delivery" && regiaoSelecionada?.bloqueado) {
      toast.error("Essa região não é atendida no momento.");
      return;
    }
    // Validação leve: se for delivery e o cliente está editando/informando endereço,
    // exigir pelo menos rua+número OU a linha livre `endereco`.
    if (f.tipo === "delivery" && modoEnd !== "cadastrado") {
      const temEstruturado = addr.rua && addr.numero_end;
      const temLivre = (addr.endereco || "").trim().length >= 5;
      if (!temEstruturado && !temLivre) {
        toast.error("Informe o endereço de entrega");
        return;
      }
    }
    setEnviando(true);
    // Endereço final para este pedido
    let addrFinal = {};
    if (f.tipo === "delivery") {
      if (modoEnd === "cadastrado") {
        addrFinal = {
          endereco: cliente.endereco || "",
          referencia: cliente.referencia || "",
          cep: cliente.cep || "",
          rua: cliente.rua || "",
          numero_end: cliente.numero_end || "",
          complemento: cliente.complemento || "",
          bairro: cliente.bairro || "",
          cidade: cliente.cidade || "",
          estado: cliente.estado || "",
          apelido: cliente.apelido || "Casa",
          latitude: cliente.latitude ?? null,
          longitude: cliente.longitude ?? null,
        };
      } else {
        addrFinal = {
          ...addr,
          // Compõe a string livre se o cliente preencheu os campos estruturados
          endereco: (addr.rua ? enderecoComposto() : addr.endereco) || addr.endereco || "",
        };
      }
    }
    const dados = {
      ...f,
      forma_pagamento: metodoOverride || f.forma_pagamento,
      salvar_endereco_no_cadastro: f.tipo === "delivery" && modoEnd === "editar",
      ...addrFinal,
    };
    if (descontoInfo) dados.cupom_codigo = f.cupom_codigo;
    if (f.tipo === "delivery" && f.regiao_id) dados.regiao_id = f.regiao_id;
    await onFinalizar(dados);
    setEnviando(false);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-auto">
        <div className="flex items-center gap-2 mb-4">
          <button onClick={onClose}><ArrowLeft className="w-5 h-5"/></button>
          <h2 className="font-black text-lg">Finalizar pedido</h2>
        </div>
        <div className="text-sm bg-neutral-50 rounded-xl p-3 mb-4">
          <b>{cliente.nome}</b> · {cliente.whatsapp}
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-xs text-neutral-500 mb-1">Tipo de entrega</div>
            <div className="flex gap-2">
              {[{k:"delivery",l:"Delivery"},{k:"retirada",l:"Retirada"}].map(t => (
                <button key={t.k} onClick={() => setF({...f, tipo: t.k})} className={`flex-1 py-2 rounded-xl text-sm font-bold ${f.tipo === t.k ? "text-white" : "bg-neutral-100"}`} style={f.tipo === t.k ? {background: cor} : {}}>
                  {t.l}
                </button>
              ))}
            </div>
          </div>
          {f.tipo === "delivery" && (
            <>
              {/* ============ Endereço de entrega — estilo iFood ============ */}
              <div className="border border-neutral-200 rounded-xl p-3 bg-neutral-50" data-testid="vaipede-pub-check-addrbox">
                <div className="flex items-center gap-1.5 mb-2">
                  <MapPin className="w-4 h-4" style={{color: cor}}/>
                  <div className="text-xs font-black text-neutral-700">ENTREGAR EM</div>
                </div>
                {/* Se tem cadastro, mostra o endereço atual em destaque */}
                {clienteTemEndereco && (
                  <div className={`rounded-xl border-2 p-3 mb-2 bg-white ${modoEnd === "cadastrado" ? "border-emerald-500" : "border-neutral-200"}`}>
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5">
                        {(cliente.apelido || "Casa").toLowerCase().includes("trabalho") ? <Briefcase className="w-4 h-4 text-neutral-500"/> : <Home className="w-4 h-4 text-neutral-500"/>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-neutral-500 uppercase">{cliente.apelido || "Casa"}</div>
                        <div className="text-sm text-neutral-800 leading-snug break-words">
                          {cliente.rua ? (
                            <>
                              {cliente.rua}{cliente.numero_end ? `, ${cliente.numero_end}` : ""}
                              {cliente.complemento ? ` — ${cliente.complemento}` : ""}<br/>
                              {cliente.bairro}{cliente.cidade ? ` · ${cliente.cidade}` : ""}{cliente.estado ? `/${cliente.estado}` : ""}
                            </>
                          ) : (
                            cliente.endereco || (cliente.latitude ? "📍 Localização GPS compartilhada" : "—")
                          )}
                        </div>
                        {cliente.referencia && <div className="text-xs text-neutral-500 mt-0.5">📌 {cliente.referencia}</div>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Opções */}
                <div className="grid grid-cols-1 gap-1.5">
                  {clienteTemEndereco && (
                    <button type="button" onClick={() => setModoEnd("cadastrado")} className={`text-left px-3 py-2 rounded-lg text-xs font-bold border-2 transition ${modoEnd === "cadastrado" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-neutral-200 bg-white text-neutral-700 hover:border-emerald-300"}`} data-testid="vaipede-pub-addr-opt-cadastrado">
                      <Check className={`w-3.5 h-3.5 inline mr-1 ${modoEnd === "cadastrado" ? "text-emerald-600" : "text-transparent"}`}/> Entregar neste endereço
                    </button>
                  )}
                  <button type="button" onClick={() => setModoEnd("editar")} className={`text-left px-3 py-2 rounded-lg text-xs font-bold border-2 transition ${modoEnd === "editar" ? "border-blue-500 bg-blue-50 text-blue-800" : "border-neutral-200 bg-white text-neutral-700 hover:border-blue-300"}`} data-testid="vaipede-pub-addr-opt-editar">
                    <Edit3 className="w-3.5 h-3.5 inline mr-1"/> {clienteTemEndereco ? "Atualizar meu endereço" : "Cadastrar meu endereço"}
                  </button>
                  <button type="button" onClick={() => setModoEnd("so_neste")} className={`text-left px-3 py-2 rounded-lg text-xs font-bold border-2 transition ${modoEnd === "so_neste" ? "border-amber-500 bg-amber-50 text-amber-800" : "border-neutral-200 bg-white text-neutral-700 hover:border-amber-300"}`} data-testid="vaipede-pub-addr-opt-so-neste">
                    <MapPin className="w-3.5 h-3.5 inline mr-1"/> Entregar em outro endereço apenas neste pedido
                  </button>
                </div>

                {/* Form de endereço (aparece quando editar/so_neste) */}
                {modoEnd !== "cadastrado" && (
                  <div className="mt-3 space-y-2" data-testid="vaipede-pub-addr-form">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 relative">
                        <input
                          value={addr.cep}
                          onChange={e => {
                            const v = e.target.value.replace(/\D/g, "").slice(0, 8);
                            const masked = v.length > 5 ? `${v.slice(0,5)}-${v.slice(5)}` : v;
                            setAddr({...addr, cep: masked});
                            if (v.length === 8) buscarCep(v);
                          }}
                          placeholder="CEP"
                          inputMode="numeric"
                          className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm"
                          data-testid="vaipede-pub-addr-cep"
                        />
                        {cepLoading && <span className="absolute right-3 top-3 text-[10px] text-neutral-400">buscando...</span>}
                      </div>
                      <select value={addr.apelido} onChange={e => setAddr({...addr, apelido: e.target.value})} className="px-2 py-2.5 rounded-lg border border-neutral-200 text-xs" data-testid="vaipede-pub-addr-apelido">
                        <option>Casa</option>
                        <option>Trabalho</option>
                        <option>Outro</option>
                      </select>
                    </div>
                    <input value={addr.rua} onChange={e => setAddr({...addr, rua: e.target.value})} placeholder="Rua / Avenida" className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-addr-rua"/>
                    <div className="grid grid-cols-3 gap-2">
                      <input value={addr.numero_end} onChange={e => setAddr({...addr, numero_end: e.target.value})} placeholder="Número" className="px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-addr-numero"/>
                      <input value={addr.complemento} onChange={e => setAddr({...addr, complemento: e.target.value})} placeholder="Complemento (apto, bloco)" className="col-span-2 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-addr-complemento"/>
                    </div>
                    <input value={addr.bairro} onChange={e => setAddr({...addr, bairro: e.target.value})} placeholder="Bairro" className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-addr-bairro"/>
                    <div className="grid grid-cols-3 gap-2">
                      <input value={addr.cidade} onChange={e => setAddr({...addr, cidade: e.target.value})} placeholder="Cidade" className="col-span-2 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-addr-cidade"/>
                      <input value={addr.estado} onChange={e => setAddr({...addr, estado: e.target.value.toUpperCase().slice(0,2)})} placeholder="UF" maxLength={2} className="px-3 py-2.5 rounded-lg border border-neutral-200 text-sm uppercase" data-testid="vaipede-pub-addr-estado"/>
                    </div>
                    <input value={addr.referencia} onChange={e => setAddr({...addr, referencia: e.target.value})} placeholder="Ponto de referência (ex: em frente à padaria)" className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-addr-referencia"/>
                    {modoEnd === "editar" && (
                      <p className="text-[11px] text-blue-700 bg-blue-50 rounded-lg p-2">
                        <b>✓</b> Este endereço será salvo no seu cadastro para próximos pedidos.
                      </p>
                    )}
                    {modoEnd === "so_neste" && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg p-2">
                        <b>!</b> Usado apenas neste pedido — seu cadastro não será alterado.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {regioes.length > 0 && (
                <div>
                  <div className="text-xs text-neutral-500 mb-1">Selecione seu bairro (taxa de entrega)</div>
                  <select value={f.regiao_id} onChange={e => setF({...f, regiao_id: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-pub-regiao">
                    <option value="">— Escolha o bairro —</option>
                    {regioes.map(r => (
                      <option key={r.id} value={r.id} disabled={r.bloqueado}>
                        {r.bairro}{r.cidade ? ` (${r.cidade})` : ""}
                        {r.bloqueado ? " 🚫 Fora da área" : r.frete_gratis ? " · 🎁 Frete grátis" : ` · ${money(Number(r.taxa || 0))}`}
                      </option>
                    ))}
                  </select>
                  {regiaoSelecionada?.bloqueado && (
                    <div className="mt-1 p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                      🚫 Ainda não atendemos essa região.{regiaoSelecionada.obs_bloqueio ? ` ${regiaoSelecionada.obs_bloqueio}` : ""}
                    </div>
                  )}
                  {faltaFreteGratis > 0 && (
                    <div className="mt-1 p-2 rounded-lg bg-emerald-50 border border-emerald-300 text-xs text-emerald-800 font-bold" data-testid="vaipede-pub-freete-gratis-nudge">
                      🎁 Adicione mais {money(faltaFreteGratis)} para ganhar <b>FRETE GRÁTIS</b>!
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Cupom do dia */}
          {(disponiveis.length > 0 || esgotados.length > 0 || f.cupom_codigo) && (
            <div className="border border-neutral-200 rounded-xl p-3">
              <div className="text-xs text-neutral-500 mb-2">🎁 Descontos do dia</div>
              {disponiveis.map(cp => (
                <button key={cp.id} onClick={() => aplicarCupom(cp.codigo)} disabled={validandoCupom} className={`w-full mb-1 px-3 py-2 rounded-lg text-sm text-left flex items-center justify-between ${f.cupom_codigo === cp.codigo ? "bg-emerald-50 border border-emerald-300" : "bg-neutral-50"}`} data-testid={`vaipede-pub-aplicar-cupom-${cp.id}`}>
                  <span><b>{cp.nome}</b> <span className="font-mono text-xs text-neutral-500">{cp.codigo}</span></span>
                  <span className="font-bold" style={{color: cor}}>{cp.tipo_desconto === "percentual" ? `-${cp.valor}%` : `-${money(cp.valor)}`}</span>
                </button>
              ))}
              {esgotados.map(cp => (
                <div key={cp.id} className="w-full mb-1 px-3 py-2 rounded-lg text-sm bg-neutral-100 opacity-60 flex items-center justify-between">
                  <span>{cp.nome} <span className="text-xs text-red-600">— esgotado hoje</span></span>
                </div>
              ))}
              {descontoInfo && (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-emerald-700 font-bold">✓ {f.cupom_codigo} aplicado</span>
                  <button onClick={removerCupom} className="text-xs text-red-500 underline">Remover</button>
                </div>
              )}
            </div>
          )}

          <div>
            <div className="text-xs text-neutral-500 mb-1">Pagar na entrega</div>
            <select value={f.forma_pagamento} onChange={e => setF({...f, forma_pagamento: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm">
              <option value="dinheiro">Dinheiro</option>
              <option value="pix">Pix</option>
              <option value="cartao_credito">Cartão de crédito</option>
              <option value="cartao_debito">Cartão de débito</option>
            </select>
          </div>
          {f.forma_pagamento === "dinheiro" && (
            <input type="text" inputMode="decimal" value={f.troco_para} onChange={e => setF({...f, troco_para: e.target.value.replace(",", ".")})} placeholder="Troco para R$ (opcional)" className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm"/>
          )}
          <textarea value={f.observacao} onChange={e => setF({...f, observacao: e.target.value})} placeholder="Observações (ex: sem cebola)" rows={2} className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm resize-none"/>
        </div>

        {descontoInfo && (
          <div className="mt-4 flex items-center justify-between text-sm text-neutral-500">
            <span>Subtotal</span><span>{money(total)}</span>
          </div>
        )}
        {descontoInfo && (
          <div className="flex items-center justify-between text-sm text-emerald-700 font-bold">
            <span>Desconto ({f.cupom_codigo})</span><span>-{money(descontoInfo.desconto)}</span>
          </div>
        )}
        {f.tipo === "delivery" && regiaoSelecionada && !regiaoSelecionada.bloqueado && (
          <div className="flex items-center justify-between text-sm mt-1">
            <span>Taxa de entrega</span>
            <span className={taxaEntrega === 0 ? "text-emerald-700 font-bold" : ""}>
              {taxaEntrega === 0 ? "🎁 GRÁTIS" : `+${money(taxaEntrega)}`}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between mt-3 text-lg"><b>Total</b><b style={{color: cor}}>{money((descontoInfo ? descontoInfo.total_com_desconto : total) + taxaEntrega)}</b></div>

        {pagamentoOnline ? (
          <>
            <button onClick={() => enviar("infinitepay")} disabled={enviando} className="w-full mt-3 py-3.5 rounded-xl text-white font-black disabled:opacity-60 flex items-center justify-center gap-2 bg-emerald-600" data-testid="vaipede-pub-pagar-agora">
              <CreditCard className="w-5 h-5"/> {enviando ? "Aguarde..." : "Pagar agora (Pix/Crédito/Débito)"}
            </button>
            <p className="text-xs text-neutral-500 mt-1 text-center">Pagamento seguro via InfinitePay · Aprovação imediata</p>
            <div className="my-3 flex items-center gap-3 text-xs text-neutral-400">
              <div className="flex-1 h-px bg-neutral-200"/>OU<div className="flex-1 h-px bg-neutral-200"/>
            </div>
          </>
        ) : (
          <div className="mt-3 mb-3 p-3 bg-neutral-100 rounded-xl text-xs text-neutral-600 text-center">
            💳 Pagamento online indisponível — pague na entrega
          </div>
        )}

        <button onClick={() => enviar()} disabled={enviando} className="w-full py-3.5 rounded-xl text-white font-black disabled:opacity-60 flex items-center justify-center gap-2" style={{background: cor}} data-testid="vaipede-pub-enviar">
          <Check className="w-5 h-5"/> {enviando ? "Enviando..." : "Enviar pedido (pagar na entrega)"}
        </button>
        <p className="text-xs text-neutral-500 mt-3 text-center">
          {loja.nome} vai receber e responder pelo WhatsApp.
        </p>
      </div>
    </div>
  );
}

function StatusTimeline({ status, eta, cor }) {
  const steps = [
    { k: "aguardando", label: "Aguardando" },
    { k: "aceito", label: "Aceito" },
    { k: "preparando", label: "Preparando" },
    { k: "pronto", label: "Pronto" },
    { k: "saiu_entrega", label: eta ? `Saiu · ${eta}min` : "Saiu" },
    { k: "entregue", label: "Entregue" },
  ];
  const order = ["aguardando", "aceito", "preparando", "pronto", "saiu_entrega", "entregue"];
  const idx = order.indexOf(status);
  if (status === "rejeitado") {
    return <div className="text-xs text-red-600 font-bold text-center py-1">❌ Pedido recusado</div>;
  }
  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => {
        const ativo = i <= idx;
        const atual = i === idx;
        return (
          <div key={s.k} className="flex-1 flex flex-col items-center">
            <div className={`w-full h-1.5 rounded-full mb-1 ${ativo ? "" : "bg-neutral-200"}`} style={ativo ? {background: cor} : {}}/>
            <span className={`text-[9px] leading-tight text-center ${ativo ? "font-bold" : "text-neutral-400"} ${atual ? "text-neutral-900" : ""}`}>{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}


const STATUS_LABEL = {
  aguardando: { txt: "Aguardando", cls: "bg-neutral-100 text-neutral-700" },
  aceito: { txt: "Aceito", cls: "bg-blue-50 text-blue-700" },
  preparando: { txt: "Preparando", cls: "bg-amber-50 text-amber-700" },
  pronto: { txt: "Pronto", cls: "bg-purple-50 text-purple-700" },
  saiu_entrega: { txt: "Saiu para entrega", cls: "bg-indigo-50 text-indigo-700" },
  entregue: { txt: "Entregue", cls: "bg-emerald-50 text-emerald-700" },
  finalizado: { txt: "Finalizado", cls: "bg-emerald-50 text-emerald-700" },
  rejeitado: { txt: "Recusado", cls: "bg-red-50 text-red-700" },
  cancelado: { txt: "Cancelado", cls: "bg-red-50 text-red-700" },
};

function HistoricoModal({ slug, cliente, cor, onClose }) {
  const [pedidos, setPedidos] = useState(null); // null=loading, []=vazio
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let ativo = true;
    const cid = cliente?.id || "x";
    const wa = cliente?.whatsapp || "";
    vpAxios.get(`/vaipede/publico/${slug}/cliente/${cid}/pedidos`, { params: { whatsapp: wa }})
      .then(r => { if (ativo) setPedidos(r.data || []); })
      .catch(() => { if (ativo) { setPedidos([]); setErro("Não foi possível carregar seu histórico"); }});
    return () => { ativo = false; };
  }, [slug, cliente]);

  const fmtData = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  const baixar = async (pid, numero) => {
    try {
      const resp = await fetch(`/api/vaipede/publico/${slug}/pedido/${pid}/recibo-termico`);
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const safeName = `pedido-${numero || pid}.pdf`;
      const file = new File([blob], safeName, { type: "application/pdf" });
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: safeName });
          return;
        }
      } catch (err) { if (err && err.name === "AbortError") return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = safeName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      try { window.open(url, "_blank", "noopener,noreferrer"); } catch {}
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch {
      toast.error("Erro ao baixar recibo");
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="vaipede-pub-historico-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <h2 className="font-black text-lg flex items-center gap-2"><History className="w-5 h-5"/> Meus pedidos</h2>
            <p className="text-xs text-neutral-500 mt-0.5">{cliente?.nome} · {cliente?.whatsapp}</p>
          </div>
          <button onClick={onClose} data-testid="vaipede-pub-historico-close"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 overflow-auto flex-1">
          {pedidos === null && (
            <div className="text-center text-neutral-500 py-10">Carregando…</div>
          )}
          {pedidos && pedidos.length === 0 && (
            <div className="text-center py-14">
              <Clock className="w-10 h-10 mx-auto text-neutral-300 mb-2"/>
              <p className="text-sm text-neutral-500">{erro || "Você ainda não fez pedidos por aqui."}</p>
              <p className="text-xs text-neutral-400 mt-1">Seu histórico aparece aqui após o primeiro pedido.</p>
            </div>
          )}
          {pedidos && pedidos.length > 0 && (
            <div className="space-y-3">
              {pedidos.map(p => {
                const st = STATUS_LABEL[p.status] || { txt: p.status, cls: "bg-neutral-100 text-neutral-700" };
                const nItens = (p.itens || []).reduce((s, i) => s + (i.quantidade || 0), 0);
                return (
                  <div key={p.id} className="border border-neutral-200 rounded-2xl p-3" data-testid={`vaipede-pub-hist-ped-${p.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <div className="font-black text-sm">Pedido #{p.numero}</div>
                        <div className="text-[11px] text-neutral-500">{fmtData(p.created_at)} · {p.tipo}</div>
                      </div>
                      <span className={`text-[11px] font-bold px-2 py-1 rounded-lg ${st.cls}`}>{st.txt}</span>
                    </div>
                    <div className="text-xs text-neutral-600 mb-2">
                      {(p.itens || []).slice(0, 3).map((it, i) => (
                        <div key={i} className="truncate">{it.quantidade}x {it.nome}</div>
                      ))}
                      {nItens > 3 && <div className="text-neutral-400">+{(p.itens || []).length - 3} outro(s)…</div>}
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
                      <span className="text-xs text-neutral-500">{nItens} {nItens === 1 ? "item" : "itens"}</span>
                      <div className="flex items-center gap-3">
                        <button onClick={() => baixar(p.id, p.numero)} className="text-xs font-bold flex items-center gap-1 hover:underline" style={{color: cor}} data-testid={`vaipede-pub-hist-recibo-${p.id}`}>
                          <Printer className="w-3 h-3"/> Recibo
                        </button>
                        <b className="text-sm" style={{color: cor}}>{money(p.total)}</b>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function MeuPerfilModal({ slug, cliente, cor, onClose, onSaved, onLogout }) {
  const [f, setF] = useState({
    nome: cliente.nome || "",
    whatsapp: cliente.whatsapp || "",
    apelido: cliente.apelido || "Casa",
    cep: cliente.cep || "",
    rua: cliente.rua || "",
    numero_end: cliente.numero_end || "",
    complemento: cliente.complemento || "",
    bairro: cliente.bairro || "",
    cidade: cliente.cidade || "",
    estado: cliente.estado || "",
    referencia: cliente.referencia || "",
    endereco: cliente.endereco || "",
  });
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  const buscarCep = async (cepClean) => {
    if (cepClean.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cepClean}/json/`);
      const d = await r.json();
      if (!d.erro) {
        setF(v => ({
          ...v,
          rua: v.rua || d.logradouro || "",
          bairro: v.bairro || d.bairro || "",
          cidade: v.cidade || d.localidade || "",
          estado: v.estado || d.uf || "",
        }));
      }
    } catch {} finally { setCepLoading(false); }
  };

  const salvar = async () => {
    if (!f.nome.trim()) return toast.error("Nome é obrigatório");
    setSaving(true);
    try {
      // Recompõe a linha livre 'endereco' se preencheu os campos estruturados
      const partes = [];
      if (f.rua) partes.push(f.rua + (f.numero_end ? `, ${f.numero_end}` : ""));
      if (f.complemento) partes.push(f.complemento);
      if (f.bairro) partes.push(f.bairro);
      if (f.cidade) partes.push(f.cidade + (f.estado ? `/${f.estado}` : ""));
      const enderecoComposto = partes.join(" — ");
      const { data } = await vpAxios.patch(
        `/vaipede/publico/${slug}/cliente/${cliente.id}`,
        {
          whatsapp_auth: cliente.whatsapp,
          nome: f.nome,
          apelido: f.apelido,
          cep: f.cep,
          rua: f.rua,
          numero_end: f.numero_end,
          complemento: f.complemento,
          bairro: f.bairro,
          cidade: f.cidade,
          estado: f.estado,
          referencia: f.referencia,
          endereco: enderecoComposto || f.endereco || "",
        }
      );
      toast.success("Perfil atualizado!");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="vaipede-pub-perfil-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <h2 className="font-black text-lg flex items-center gap-2"><User className="w-5 h-5"/> Meu perfil</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Mantenha seus dados atualizados</p>
          </div>
          <button onClick={onClose} data-testid="vaipede-pub-perfil-close"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 overflow-auto flex-1 space-y-3">
          <div>
            <label className="text-xs text-neutral-500 font-bold">Nome</label>
            <input value={f.nome} onChange={e => setF({...f, nome: e.target.value})} className="w-full mt-1 px-4 py-3 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-pub-perfil-nome"/>
          </div>
          <div>
            <label className="text-xs text-neutral-500 font-bold">WhatsApp <span className="text-neutral-400">(não editável)</span></label>
            <input value={f.whatsapp} disabled className="w-full mt-1 px-4 py-3 rounded-xl border border-neutral-200 text-sm bg-neutral-50 text-neutral-500" data-testid="vaipede-pub-perfil-whatsapp"/>
          </div>

          <div className="pt-2 border-t border-neutral-100">
            <div className="flex items-center gap-1.5 mb-2">
              <MapPin className="w-4 h-4" style={{color: cor}}/>
              <div className="text-xs font-black text-neutral-700">MEU ENDEREÇO</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 relative">
                <input
                  value={f.cep}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 8);
                    const masked = v.length > 5 ? `${v.slice(0,5)}-${v.slice(5)}` : v;
                    setF({...f, cep: masked});
                    if (v.length === 8) buscarCep(v);
                  }}
                  placeholder="CEP"
                  inputMode="numeric"
                  className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm"
                  data-testid="vaipede-pub-perfil-cep"
                />
                {cepLoading && <span className="absolute right-3 top-3 text-[10px] text-neutral-400">buscando...</span>}
              </div>
              <select value={f.apelido} onChange={e => setF({...f, apelido: e.target.value})} className="px-2 py-2.5 rounded-lg border border-neutral-200 text-xs" data-testid="vaipede-pub-perfil-apelido">
                <option>Casa</option>
                <option>Trabalho</option>
                <option>Outro</option>
              </select>
            </div>
            <input value={f.rua} onChange={e => setF({...f, rua: e.target.value})} placeholder="Rua / Avenida" className="w-full mt-2 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-perfil-rua"/>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <input value={f.numero_end} onChange={e => setF({...f, numero_end: e.target.value})} placeholder="Número" className="px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-perfil-numero"/>
              <input value={f.complemento} onChange={e => setF({...f, complemento: e.target.value})} placeholder="Complemento (apto, bloco)" className="col-span-2 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-perfil-complemento"/>
            </div>
            <input value={f.bairro} onChange={e => setF({...f, bairro: e.target.value})} placeholder="Bairro" className="w-full mt-2 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-perfil-bairro"/>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <input value={f.cidade} onChange={e => setF({...f, cidade: e.target.value})} placeholder="Cidade" className="col-span-2 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-perfil-cidade"/>
              <input value={f.estado} onChange={e => setF({...f, estado: e.target.value.toUpperCase().slice(0,2)})} placeholder="UF" maxLength={2} className="px-3 py-2.5 rounded-lg border border-neutral-200 text-sm uppercase" data-testid="vaipede-pub-perfil-estado"/>
            </div>
            <input value={f.referencia} onChange={e => setF({...f, referencia: e.target.value})} placeholder="Ponto de referência" className="w-full mt-2 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" data-testid="vaipede-pub-perfil-referencia"/>
          </div>
        </div>
        <div className="p-5 border-t border-neutral-100 space-y-2">
          <button onClick={salvar} disabled={saving} className="w-full py-3 rounded-xl text-white font-black disabled:opacity-60" style={{background: cor}} data-testid="vaipede-pub-perfil-salvar">
            {saving ? "Salvando..." : "Salvar alterações"}
          </button>
          {onLogout && (
            <button onClick={onLogout} className="w-full py-2 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-bold text-xs" data-testid="vaipede-pub-perfil-logout">
              Sair da conta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
